import { requestPayment } from 'Cloud/Application/Payment';
import classNames from 'clsx';
import { addDays } from 'date-fns';
import store from 'lib/store';
import React, { memo, ReactElement, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGoToSubscriptions } from 'reactApp/hooks/useGoToSubscriptions';
import { initProducts } from 'reactApp/modules/products/products.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import Dialog from 'reactApp/ui/Dialog/Dialog';
import { useTariffs } from 'reactApp/ui/QuotaPopup/useTariffs';
import { TariffCard } from 'reactApp/ui/TariffCard/TariffCard';
import { createGaSender } from 'reactApp/utils/ga';
import { EPaymentGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { getTariffProductsByPeriods } from 'reactApp/utils/Period';

import styles from './QuotaPopup.css';

const sendGa = createGaSender('quota-banner');

interface Props {
    quota: 2 | 4 | 8 | 16;
    onClose: () => void;
}

// eslint-disable-next-line max-lines-per-function
export const QuotaPopup = memo(({ quota, onClose }: Props): ReactElement | null => {
    const dispatch = useDispatch();
    const { total } = useSelector(UserSelectors.getCloudSpace);
    const userEmail = useSelector(UserSelectors.getEmail);
    const { goToSubscriptions } = useGoToSubscriptions();

    const showPro = total.space >= 512 && (quota === 8 || quota === 16);
    const showDiscount = !showPro && (quota === 8 || quota === 16);

    const tariffs = useTariffs({ isPro: showPro, isDiscount: showDiscount }) || [];

    const setStore = useCallback(
        (dayCount: number): void => {
            if (userEmail) {
                const now = new Date();
                const endDate = addDays(now, dayCount).valueOf();
                store.set(`${userEmail}|quota-banner_${quota}`, endDate);
            }
        },
        [quota, userEmail]
    );

    useEffect(() => {
        dispatch(initProducts());

        sendGa('show');
        sendPaymentGa({ action: EPaymentGa.quotaBanner, label: 'view' });

        if (quota === 2) {
            setStore(1);
        } else {
            setStore(7);
        }
    }, []);

    const getDescriptions = () => {
        const tariff = tariffs[0];

        if (showPro) {
            return `Подключите PRO тариф на ${tariff.space.value} – с ним вы не только
                    <br />
                    увеличите Облако, но и получите подписку на Combo
                    <br />и Диск-О:. И все это по сниженной цене!`;
        } else if (showDiscount) {
            return `Увеличьте его на ${tariff.space.value} со скидкой`;
        }

        return `
                Скоро квота закончится, и вы не сможете загружать новые файлы и делиться
                <br />
                старыми. Удалите лишнее или увеличьте Облако`;
    };

    const handleOnTariffsClick = useCallback((): void => {
        sendGa('click-all');
        sendPaymentGa({ action: EPaymentGa.quotaBanner, label: 'click_more' });
        if (quota !== 2) {
            setStore(3);
        }

        goToSubscriptions({ inSameTab: true });
        onClose();
    }, [quota, setStore, onClose]);

    const handleOnClose = useCallback((): void => {
        sendGa('close');
        sendPaymentGa({ action: EPaymentGa.quotaBanner, label: 'close' });
        onClose();
    }, [onClose]);

    const handleOnBuyClick = useCallback(
        (productId): void => {
            if (quota !== 2) {
                setStore(3);
            }

            sendGa('click');
            sendPaymentGa({ action: EPaymentGa.quotaBanner, label: 'click', tariff: productId });

            onClose();

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            requestPayment({
                productId,
                forceFastCheckout: true,
                onSuccess: () => {
                    sendGa('success');
                },
            });
        },
        [onClose, quota, setStore]
    );

    const renderTariffs = () => {
        return tariffs.map((tariff) => {
            const { yearProduct } = getTariffProductsByPeriods(tariff.products);

            const saving = yearProduct.discountPrice ? yearProduct.price - yearProduct.discountPrice || 11 : null;

            return (
                <div key={tariff.id}>
                    <TariffCard
                        id={yearProduct.id}
                        tariffId={tariff.id}
                        flags={tariff.flags}
                        space={yearProduct.space}
                        price={yearProduct.price}
                        discountPrice={yearProduct.discountPrice}
                        showSaving={!!saving}
                        saving={saving}
                        onBuyClick={handleOnBuyClick}
                        showOldPrice={false}
                        period={yearProduct.period}
                        isPrimary
                        isVisible
                        withoutTariffIcon
                        withoutTariffFeatures
                        isProfessional={yearProduct.isProfessional}
                        modificator="quota"
                        topLabel
                        forceShowPeriod
                        isYear
                    />
                </div>
            );
        });
    };

    if (!tariffs.length) {
        return null;
    }

    return (
        <Dialog
            open
            closeOnDimmerClick
            closeOnEscape
            id="quota-popup"
            theme="octavius"
            showCloseIcon
            size="middle"
            onCancel={handleOnClose}
            className={classNames({
                [styles.modal]: !showDiscount && !showPro,
            })}
        >
            <div className={styles.root}>
                <div className={styles.title}>
                    В вашем Облаке свободно менее <span className={styles.quota}>{quota} ГБ</span>
                </div>
                <div className={styles.descriptions} dangerouslySetInnerHTML={{ __html: getDescriptions() }} />
                <div className={styles.tariffs}>{renderTariffs()}</div>
                <div className={styles.allTariffs} onClick={handleOnTariffsClick}>
                    Посмотреть все тарифы
                </div>
            </div>
        </Dialog>
    );
});

QuotaPopup.displayName = 'QuotaPopup';
